import type { WidgetProps } from '@rjsf/utils';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import { TypographyVariant } from '../../../utils/theme';
import { getFileAsDataUrlWithName } from '../../../utils/get-file-as-data-url-with-name';
import type { EditableFile } from '../../file-upload-lists';
import { DetailedFileUploadList } from '../../file-upload-lists';
import type { IFileUploader } from '../../file-uploader/file-uploader';
import FileUploader from '../../file-uploader/file-uploader';
import InputLabel from '../../input-label/input-label';
import Typography from '../../typography/typography';
import { extractFileNameFromDataUrl, parseExistingFile } from '../utils';
import {
  ALL_FILE_EXTENSIONS,
  InputWrapper,
  Wrapper,
} from './file-uploader-widget';

export interface DetailedFile {
  document: string;
  name: string;
  description?: string;
  isPolicyDocument?: boolean;
}

interface DetailedFileUploaderWidgetProps extends Omit<WidgetProps, 'value'> {
  value?: DetailedFile[];
}

const DetailedFileUploaderWidget = ({
  value,
  label,
  onChange,
  disabled,
  rawErrors,
  hideError,
  uiSchema,
  registry,
  schema,
  readonly,
}: DetailedFileUploaderWidgetProps) => {
  const formContext = registry?.formContext;
  const theme = useTheme();
  const { t } = useTranslation();
  const [isUploading, setIsUploading] = useState(false);
  const hasError = Boolean(rawErrors && rawErrors.length > 0 && !hideError);
  const currentValue = useMemo(() => value ?? [], [value]);

  const handleFileUpload = useCallback(
    async (files: File[]) => {
      setIsUploading(true);
      const filesAsDataUrls = await Promise.all(
        files.map((file) => getFileAsDataUrlWithName(file))
      );
      const dataUrlsWithNameAndDescription = files.map((file, index) => {
        return {
          name: file.name,
          description: undefined,
          document: filesAsDataUrls[index],
          isPolicyDocument: false,
        };
      });
      onChange([...currentValue, ...dataUrlsWithNameAndDescription]);
      setIsUploading(false);
    },
    [onChange, currentValue]
  );

  const files: EditableFile[] = useMemo(() => {
    return currentValue.map((file: DetailedFile, index: number) => {
      // for when BE sends existing files and parseFormValues has converted to data url
      if (file.document.startsWith('data:text/url')) {
        return {
          ...file,
          ...parseExistingFile(file.document),
        };
      }
      return {
        ...file,
        file: file.document,
        uuid: String(index),
        originalName: extractFileNameFromDataUrl(file.document, index),
      };
    });
  }, [currentValue]);

  const handleDelete = useCallback(
    (index: number) => {
      onChange([
        ...currentValue.slice(0, index),
        ...currentValue.slice(index + 1),
      ]);
    },
    [onChange, currentValue]
  );

  const handleChange = useCallback(
    (index: number, fileChanges: Partial<EditableFile>) => {
      onChange(
        currentValue.map((originalFile: DetailedFile, valueIndex: number) => {
          if (index === valueIndex) {
            return {
              ...originalFile,
              ...fileChanges,
            };
          }
          return originalFile;
        })
      );
    },
    [onChange, currentValue]
  );

  const fileUploaderProps: IFileUploader = {
    uploading: isUploading,
    onFileUpload: handleFileUpload,
    acceptedFileExtensions:
      uiSchema?.acceptFileTypes ??
      formContext?.acceptFileTypes ??
      ALL_FILE_EXTENSIONS,
    maxFileSize: uiSchema?.maxFileSize,
    uploadError: hasError ? rawErrors?.join(', ') : undefined,
    disabled: disabled || readonly,
    name: label,
  };

  return (
    <Wrapper>
      <InputLabel theme={theme} $hasError={hasError}>
        {label}
      </InputLabel>
      <Typography
        variant={TypographyVariant.bodySm1}
        color={theme.palette.neutral[90]}
      >
        {schema?.description}
      </Typography>
      <InputWrapper>
        <Wrapper>
          <FileUploader {...fileUploaderProps} />
          {files.length > 0 && (
            <DetailedFileUploadList
              disabled={disabled || readonly}
              files={files}
              onDelete={handleDelete}
              onSave={handleChange}
              copy={{
                edit: t('words.edit', {
                  defaultValue: 'Edit',
                }),
                delete: t('words.delete', {
                  defaultValue: 'Delete',
                }),
                noDescription: t('documents.noDescription', {
                  defaultValue: '(No description)',
                }),
              }}
            />
          )}
        </Wrapper>
      </InputWrapper>
    </Wrapper>
  );
};

export default DetailedFileUploaderWidget;
